import { useEffect, useState } from "react";

/**
 * Global "is the soft keyboard open?" signal.
 *
 * On Capacitor (iOS/Android) this is driven by the @capacitor/keyboard
 * plugin's keyboardWillShow / keyboardWillHide events — the authoritative
 * source on native, where it fires before the keyboard animates in/out so the
 * UI (e.g. hiding the bottom nav) can move in lockstep with the keyboard.
 *
 * On web (and in the dev browser) the plugin events never fire, so we fall
 * back to a visualViewport resize heuristic: when the viewport shrinks past a
 * threshold the on-screen keyboard is assumed open. This is best-effort — the
 * native behaviour is what ships — but it keeps the dev experience sane.
 *
 * Consumed centrally by the shell (TabBar) so the bottom nav never floats
 * above the keyboard. A single shared hook keeps the logic in one place rather
 * than scattered per-page listeners.
 */
export function useKeyboardVisible(): boolean {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const subs: { remove: () => void }[] = [];

    // Native: @capacitor/keyboard events. Dynamically imported so a web build
    // (where the plugin is absent) simply falls through to the viewport path.
    void (async () => {
      try {
        const mod: any = await import("@capacitor/keyboard" as any);
        const Keyboard = mod?.Keyboard ?? mod;
        if (!Keyboard?.addListener) return;
        const show = await Keyboard.addListener("keyboardWillShow", () => {
          if (!cancelled) setVisible(true);
        });
        const hide = await Keyboard.addListener("keyboardWillHide", () => {
          if (!cancelled) setVisible(false);
        });
        if (cancelled) {
          show?.remove?.();
          hide?.remove?.();
          return;
        }
        subs.push(show, hide);
      } catch {
        /* not a Capacitor runtime — viewport fallback below handles web. */
      }
    })();

    // Web fallback: track the visual viewport. When the keyboard opens the
    // visual viewport height drops well below the layout viewport height.
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    let baseline = vv?.height ?? 0;
    const onResize = () => {
      if (!vv) return;
      // A shrink of more than ~150px below the tallest seen height is a
      // keyboard, not browser chrome. Track the max so rotation/chrome changes
      // re-baseline rather than latching the nav hidden.
      baseline = Math.max(baseline, vv.height);
      setVisible(baseline - vv.height > 150);
    };
    vv?.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      for (const s of subs) s?.remove?.();
      vv?.removeEventListener("resize", onResize);
    };
  }, []);

  return visible;
}
